import { HoverCard, HoverCardTrigger, HoverCardContent } from './chunk-5EZ6UUAR.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-ZGQFIZ33.js';
import { Badge } from './chunk-S775MXMS.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React2 from 'react';
import { useState, useEffect } from 'react';
import { getChainMetadata } from '@authe/fe-commons/chains';
import { resolveChainLogoUrl } from '@authe/fe-commons/assets';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { isPrivateAssetCdnKey, prepareSignedCdnImage, cdnBaseUrl, signedCdnUrl } from '@authe/fe-commons/storage';
import { RiskLevel, CatalogLifecycle, Severity } from '@authe/fe-commons/precedence';
import { Boxes, Star } from 'lucide-react';
import { ConfidenceTier, coerceConfidence, clampConfidence, formatConfidence, tierFor } from '@authe/fe-commons/evidence';
import { BUNDLE_KEY } from '@authe/fe-commons/attribution';
import { resolveAttributionChipPrefs } from '@authe/fe-commons/display';

var SIZES = {
  sm: { box: "h-4 w-4 text-[10px]", text: "text-xs" },
  md: { box: "h-6 w-6 text-xs", text: "text-sm" },
  lg: { box: "h-8 w-8 text-sm", text: "text-base" }
};
function ChainAvatar({
  chain,
  size = "md",
  showName = true,
  logoUrl,
  logoResolver,
  className
}) {
  const resolved = getChainMetadata(chain);
  const name = resolved?.name ?? chain;
  const initials = (resolved?.nativeToken ?? chain.slice(0, 3)).slice(0, 3).toUpperCase();
  const sz = SIZES[size];
  const candidate = logoUrl ?? logoResolver?.(chain, resolved?.slug) ?? resolved?.logoUrl ?? resolveChainLogoUrl(chain, resolved?.slug) ?? void 0;
  const brand = resolved?.brandColor ?? void 0;
  const [failedSrc, setFailedSrc] = React2.useState(null);
  const logo = candidate && candidate !== failedSrc ? candidate : void 0;
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-2", className), title: name, children: [
    logo ? /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        alt: name,
        "aria-hidden": true,
        className: cn("rounded-full object-cover", sz.box),
        onError: () => setFailedSrc(candidate ?? null)
      }
    ) : /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: cn(
          "inline-flex items-center justify-center rounded-full font-semibold",
          !brand && "bg-muted text-muted-foreground",
          sz.box
        ),
        style: brand ? { backgroundColor: `${brand}22`, color: brand } : void 0,
        children: initials
      }
    ),
    showName && /* @__PURE__ */ jsx("span", { className: cn("font-medium", sz.text), children: name })
  ] });
}
function isResolvedUrl(value) {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//");
}
function keyFromLogoUrl(logoUrl) {
  if (logoUrl.startsWith("data:") || logoUrl.startsWith("blob:")) return null;
  if (!isResolvedUrl(logoUrl)) return logoUrl.replace(/^\/+/, "") || null;
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const path = new URL(logoUrl, origin).pathname.replace(/^\/+/, "");
    return path || null;
  } catch {
    return null;
  }
}
function useSignedCdnImage(logoUrl) {
  const key = logoUrl ? keyFromLogoUrl(logoUrl) : null;
  const isPrivate = key != null && isPrivateAssetCdnKey(key);
  const publicSrc = logoUrl && !isPrivate && !isResolvedUrl(logoUrl) && key ? publicCdnSrc(key) : logoUrl;
  const [priv, setPriv] = useState(null);
  useEffect(() => {
    if (!isPrivate || !key) {
      setPriv(null);
      return;
    }
    let cancelled = false;
    setPriv(null);
    void prepareSignedCdnImage(key).then((r) => {
      if (!cancelled) setPriv(r);
    });
    return () => {
      cancelled = true;
    };
  }, [isPrivate, key]);
  if (!logoUrl) return { denied: false };
  if (!isPrivate) return { src: publicSrc, denied: false };
  if (!priv) return { denied: false };
  return { src: priv.src, crossOrigin: priv.crossOrigin, denied: priv.denied };
}
function publicCdnSrc(key) {
  const base = cdnBaseUrl();
  return base ? signedCdnUrl(key, base) : void 0;
}
var STYLES = {
  [RiskLevel.SAFE]: "bg-risk-none/15 text-risk-none border-risk-none/30",
  [RiskLevel.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [RiskLevel.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [RiskLevel.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [RiskLevel.SEVERE]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
var LABELS = {
  [RiskLevel.SAFE]: "Safe",
  [RiskLevel.LOW]: "Low",
  [RiskLevel.MEDIUM]: "Medium",
  [RiskLevel.HIGH]: "High",
  [RiskLevel.SEVERE]: "Severe"
};
function RiskBadge({ level, score, className }) {
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border", STYLES[level], className), children: [
    /* @__PURE__ */ jsx("span", { children: LABELS[level] }),
    typeof score === "number" && /* @__PURE__ */ jsx("span", { className: "ml-1 opacity-70", children: score.toFixed(0) })
  ] });
}
var STYLES2 = {
  [CatalogLifecycle.VERIFIED]: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  [CatalogLifecycle.CANDIDATE]: "text-muted-foreground border-border",
  [CatalogLifecycle.DEPRECATED]: "text-amber-700 dark:text-amber-400 border-amber-500/40",
  [CatalogLifecycle.QUARANTINED]: "bg-destructive/10 text-destructive border-destructive/30"
};
var LABELS2 = {
  [CatalogLifecycle.VERIFIED]: "Verified",
  [CatalogLifecycle.CANDIDATE]: "Candidate",
  [CatalogLifecycle.DEPRECATED]: "Deprecated",
  [CatalogLifecycle.QUARANTINED]: "Quarantined"
};
function LifecycleBadge({ lifecycle, className }) {
  const key = lifecycle ?? CatalogLifecycle.CANDIDATE;
  const style = STYLES2[key] ?? "text-muted-foreground border-border";
  const label = LABELS2[key] ?? String(lifecycle ?? "candidate");
  return /* @__PURE__ */ jsx(Badge, { variant: "outline", className: cn("border text-[11px] capitalize", style, className), children: label });
}
function ChipRemoveButton({
  label,
  onRemove,
  disabled,
  className
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
      },
      disabled,
      "aria-label": `Remove ${label}`,
      className: cn(
        "ml-0.5 opacity-70 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40",
        className
      ),
      children: "\xD7"
    }
  );
}
var SEVERITY_STYLES = {
  [Severity.INFO]: "bg-muted text-muted-foreground border-muted",
  [Severity.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [Severity.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [Severity.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [Severity.CRITICAL]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
function TagGlyph({
  logo,
  logoCrossOrigin,
  color,
  label
}) {
  if (logo) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
      }
    );
  }
  if (color) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        title: label,
        className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    );
  }
  return null;
}
function Tag({
  label,
  severity = Severity.INFO,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border gap-1", SEVERITY_STYLES[severity], className), children: [
    /* @__PURE__ */ jsx(TagGlyph, { logo, logoCrossOrigin, color, label }),
    /* @__PURE__ */ jsx("span", { children: label }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var SCOPE_STYLES = {
  user: { ring: "border-brand/40", dot: "bg-brand", title: "Your override" },
  tenant: { ring: "border-finality-confirmed/40", dot: "bg-finality-confirmed", title: "Tenant override" },
  org: { ring: "border-finality-pending/40", dot: "bg-finality-pending", title: "Org override" },
  provider: { ring: "border-risk-medium/40", dot: "bg-risk-medium", title: "Provider candidate" },
  entity: { ring: "border-finality-finalized/40", dot: "bg-finality-finalized", title: "Inherited from entity" },
  default: { ring: "border-muted", dot: "bg-muted-foreground/50", title: "Default" }
};
function CatalogLabel({
  label,
  value,
  scope,
  showScope,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  const style = scope ? SCOPE_STYLES[scope] : null;
  const withScope = showScope ?? Boolean(scope);
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("gap-1.5 border", style?.ring, className), children: [
    logo ? /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
      }
    ) : color ? /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    ) : null,
    withScope && style && /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: cn("h-1.5 w-1.5 rounded-full", style.dot), title: style.title }),
    /* @__PURE__ */ jsx("span", { className: "font-medium", children: label }),
    value != null && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: value }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var ACCENTS = [
  "text-sky-600 bg-sky-500/10 border-sky-500/30",
  "text-violet-600 bg-violet-500/10 border-violet-500/30",
  "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  "text-amber-600 bg-amber-500/10 border-amber-500/30",
  "text-rose-600 bg-rose-500/10 border-rose-500/30",
  "text-cyan-600 bg-cyan-500/10 border-cyan-500/30",
  "text-fuchsia-600 bg-fuchsia-500/10 border-fuchsia-500/30"
];
function accentFor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = hash * 31 + id.charCodeAt(i) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
}
function GroupBadge({
  groupId,
  name,
  count,
  icon,
  onClick,
  onRemove,
  removeDisabled,
  className
}) {
  const accent = accentFor(groupId);
  const label = name ?? groupId;
  const body = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon ?? /* @__PURE__ */ jsx(Boxes, { className: "h-3 w-3" }),
    /* @__PURE__ */ jsx("span", { children: label }),
    count != null && /* @__PURE__ */ jsxs("span", { className: "opacity-70", children: [
      "\xB7 ",
      count
    ] })
  ] });
  const shell = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
    accent,
    className
  );
  if (onClick) {
    return /* @__PURE__ */ jsxs("span", { className: cn(shell, "pr-1"), title: label, children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick,
          className: "inline-flex items-center gap-1.5 transition-opacity hover:opacity-80",
          children: body
        }
      ),
      onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { className: cn(shell, onRemove && "pr-1"), title: label, children: [
    body,
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
function splitOverflow(total, max) {
  const safeMax = Math.max(0, max);
  if (total <= safeMax) return { visible: total, overflow: 0 };
  const visible = Math.max(0, safeMax - 1);
  return { visible, overflow: total - visible };
}
function TagList({
  items,
  max = 3,
  popoverTitle,
  className,
  align = "start"
}) {
  if (items.length === 0) return null;
  const { visible, overflow } = splitOverflow(items.length, max);
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex flex-wrap items-center gap-1", className), children: [
    items.slice(0, visible).map((item, i) => /* @__PURE__ */ jsx(React2.Fragment, { children: item }, i)),
    overflow > 0 && /* @__PURE__ */ jsxs(Popover, { children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: (e) => e.stopPropagation(),
          className: "inline-flex",
          "aria-label": `Show ${overflow} more`,
          children: /* @__PURE__ */ jsxs(
            Badge,
            {
              variant: "outline",
              className: "cursor-pointer text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              children: [
                "+",
                overflow
              ]
            }
          )
        }
      ) }),
      /* @__PURE__ */ jsxs(PopoverContent, { align, className: "w-auto max-w-xs p-2", children: [
        popoverTitle && /* @__PURE__ */ jsx("div", { className: "mb-2 px-1 text-xs font-medium text-muted-foreground", children: popoverTitle }),
        /* @__PURE__ */ jsx("div", { className: "flex max-h-64 flex-wrap gap-1 overflow-y-auto", children: items.map((item, i) => /* @__PURE__ */ jsx(React2.Fragment, { children: item }, i)) })
      ] })
    ] })
  ] });
}
var KIND_PHRASE = {
  registry: "pinned by the protocol registry",
  abi: "decoded from a verified ABI",
  proxy_abi: "decoded from the proxy implementation ABI",
  wire_layout: "decoded from a fixed wire layout",
  provided: "supplied by an upstream producer",
  generic: "built by the generic fallback",
  heuristic: "inferred heuristically",
  provider: "asserted by provider",
  user: "asserted by a human",
  automation: "asserted by a trusted process",
  unknown: "of unknown origin"
};
var TIER_BAR = {
  [ConfidenceTier.HIGH]: "bg-emerald-500",
  [ConfidenceTier.MEDIUM]: "bg-amber-500",
  [ConfidenceTier.LOW]: "bg-destructive"
};
function explainEvidence(ev) {
  const source = ev.source ?? {};
  const phrase = source.kind ? KIND_PHRASE[source.kind] ?? source.kind : "of unknown origin";
  const who = source.id ? `${phrase} ${source.id}` : phrase;
  const score = coerceConfidence(ev.confidence);
  const bullets = [
    score == null ? who : `${who} (confidence ${score.toFixed(2)})`
  ];
  for (const reason of ev.reasons ?? []) bullets.push(reason);
  const active = Object.entries(ev.signals ?? {}).filter(([, v]) => Boolean(v)).map(([k]) => k).sort();
  if (active.length > 0) bullets.push(`signals: ${active.join(", ")}`);
  if (source.url) bullets.push(`source: ${source.url}`);
  return bullets;
}
function byConfidence(evidence) {
  return [...evidence].sort(
    (a, b) => (coerceConfidence(b.confidence) ?? 0) - (coerceConfidence(a.confidence) ?? 0)
  );
}
function WhyPanel({
  explanation,
  evidence,
  confidence,
  lifecycle,
  title = "Why?",
  className
}) {
  const evidenceList = React2.useMemo(() => {
    if (!evidence) return [];
    return Array.isArray(evidence) ? evidence : [evidence];
  }, [evidence]);
  const bullets = React2.useMemo(() => {
    const persisted = (explanation ?? []).filter(Boolean);
    if (persisted.length > 0) return persisted;
    return byConfidence(evidenceList).flatMap((ev) => explainEvidence(ev).map((b) => `- ${b}`));
  }, [explanation, evidenceList]);
  const score = coerceConfidence(confidence);
  if (bullets.length === 0 && score == null && !lifecycle) return null;
  const pct = score == null ? null : clampConfidence(score);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2 text-xs", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: title }),
      lifecycle && /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle })
    ] }),
    pct != null && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-[11px] text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { children: "Confidence" }),
        /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: formatConfidence(pct) })
      ] }),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "h-1.5 w-full overflow-hidden rounded-full bg-muted",
          role: "progressbar",
          "aria-valuenow": Math.round(pct * 100),
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          children: /* @__PURE__ */ jsx(
            "div",
            {
              className: cn("h-full rounded-full", TIER_BAR[tierFor(pct)]),
              style: { width: `${Math.round(pct * 100)}%` }
            }
          )
        }
      )
    ] }),
    bullets.length > 0 ? /* @__PURE__ */ jsx("ul", { className: "space-y-1 text-muted-foreground", children: bullets.map((bullet, i) => /* @__PURE__ */ jsx("li", { className: "leading-snug", children: bullet.replace(/^-\s*/, "\u2022 ") }, i)) }) : /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "No provenance recorded." })
  ] });
}
function WhyPopover({
  children,
  ...panel
}) {
  const content = WhyPanel(panel);
  if (content == null) return null;
  return /* @__PURE__ */ jsxs(HoverCard, { children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: children ?? /* @__PURE__ */ jsx(
      Badge,
      {
        variant: "outline",
        className: "cursor-help border-dashed text-[10px] text-muted-foreground",
        children: "Why?"
      }
    ) }),
    /* @__PURE__ */ jsx(HoverCardContent, { align: "start", className: "w-72", children: content })
  ] });
}
function primaryFirst(items) {
  return [...items].sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
}
function ChipSlot({
  kind,
  item,
  prefs,
  renderChip
}) {
  const img = useSignedCdnImage(prefs.logos ? item.logo : void 0);
  const glyph = prefs.logos ? { logo: img.src, logoCrossOrigin: img.crossOrigin, color: item.color } : {};
  const chip = explained(chipFor(kind, item, prefs, glyph), item, prefs);
  return /* @__PURE__ */ jsx(Fragment, { children: renderChip ? renderChip(chip, item, kind) : chip });
}
function explained(chip, item, prefs) {
  const explanation = item.explanation?.filter(Boolean) ?? [];
  const scored = typeof item.confidence === "number" && !Number.isNaN(item.confidence);
  if (!prefs.showWhy || explanation.length === 0 && !scored && !item.lifecycle) return chip;
  return /* @__PURE__ */ jsx(
    WhyPopover,
    {
      confidence: item.confidence,
      explanation,
      lifecycle: item.lifecycle,
      title: item.name,
      children: /* @__PURE__ */ jsx("span", { className: "inline-flex cursor-help", children: chip })
    }
  );
}
function chipFor(kind, item, prefs, glyph) {
  switch (kind) {
    case "label":
      return /* @__PURE__ */ jsx(
        CatalogLabel,
        {
          label: item.name,
          value: item.value,
          scope: item.scope,
          showScope: prefs.showProvenance && Boolean(item.scope),
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "tag":
      return /* @__PURE__ */ jsx(
        Tag,
        {
          label: item.name,
          severity: item.severity,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "group":
      return /* @__PURE__ */ jsx(
        GroupBadge,
        {
          groupId: item.id,
          name: item.name,
          count: item.count,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled
        }
      );
    case "entity":
      return /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "gap-1.5 font-normal", title: item.entityType ?? item.name, children: [
        item.isPrimary && // The headline entity for this address, not merely one it carries.
        // Sorting it first is not enough on a row of three — the star is
        // what says which one the reconciler will actually use.
        /* @__PURE__ */ jsx(Star, { "aria-label": "Primary", className: "-ml-0.5 h-3 w-3 shrink-0 fill-current text-amber-500" }),
        glyph.logo ? /* @__PURE__ */ jsx(
          "img",
          {
            src: glyph.logo,
            ...glyph.logoCrossOrigin ? { crossOrigin: glyph.logoCrossOrigin } : {},
            alt: "",
            "aria-hidden": true,
            className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
          }
        ) : glyph.color ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
            style: { backgroundColor: glyph.color }
          }
        ) : null,
        /* @__PURE__ */ jsx("span", { children: item.name }),
        item.onRemove && /* @__PURE__ */ jsx(
          ChipRemoveButton,
          {
            label: item.name,
            onRemove: item.onRemove,
            disabled: item.removeDisabled
          }
        )
      ] });
  }
}
function AttributionChips({
  bundle,
  userPrefs,
  popoverTitle = "Attribution",
  renderChip,
  empty = null,
  className,
  ...override
}) {
  const prefs = resolveAttributionChipPrefs(override, userPrefs);
  const items = React2.useMemo(() => {
    const out = [];
    for (const kind of prefs.kinds) {
      for (const item of primaryFirst(bundle[BUNDLE_KEY[kind]] ?? [])) {
        out.push(
          /* @__PURE__ */ jsx(
            ChipSlot,
            {
              kind,
              item,
              prefs,
              renderChip
            },
            `${kind}:${item.id}`
          )
        );
      }
    }
    return out;
  }, [bundle, prefs, renderChip]);
  if (items.length === 0) return /* @__PURE__ */ jsx(Fragment, { children: empty });
  return /* @__PURE__ */ jsx(
    TagList,
    {
      items,
      max: prefs.max,
      popoverTitle,
      className: cn(prefs.size === "sm" && "text-xs", className)
    }
  );
}

export { AttributionChips, CatalogLabel, ChainAvatar, ChipRemoveButton, GroupBadge, LifecycleBadge, RiskBadge, Tag, TagList, WhyPanel, WhyPopover, explainEvidence, splitOverflow, useSignedCdnImage };
//# sourceMappingURL=chunk-T7CLNATI.js.map
//# sourceMappingURL=chunk-T7CLNATI.js.map